import { useCallback } from "react";

import { Button, ButtonsWrapper, Info } from "components/elements";
import { WarningModal } from "components/modals";
import { ActionMenu, MenuItem } from "components/popups";
import { RestrictedArea, ROLES } from "containers/Permissions";
import { useDeleteProvider } from "hooks/tivoApps";
import { TiVoAppProvider } from "hooks/tivoApps/types";
import { useToggle } from "hooks/utils";

import { MAIN_ROUTE } from "../constants";
import { MENU, TEXTS } from "./constants";
import styles from "./TiVoAppRowMenu.scss";

interface TiVoAppRowMenuProps {
  id: TiVoAppProvider["id"];
}

export function TiVoAppRowMenu({ id }: TiVoAppRowMenuProps) {
  const {
    mutate: archive,
    isPending: archivePending,
    error: archiveError,
    reset: archiveReset,
  } = useDeleteProvider(id.toString());
  const [archiveModalOpen, toggleArchiveModalOpen] = useToggle(false);
  const warningModalDataTestId = "delete-provider-modal";

  const handleArchive = useCallback(() => {
    archive(undefined, {
      onSuccess: toggleArchiveModalOpen,
    });
  }, [archive, toggleArchiveModalOpen]);

  return (
    <>
      <WarningModal
        actions={
          <ButtonsWrapper position="modal">
            <Button
              type="normal"
              dataTestId={`${warningModalDataTestId}-cancel-button`}
              onClick={toggleArchiveModalOpen}
            >
              {TEXTS.CANCEL_BUTTON_LABEL}
            </Button>
            <Button
              disabled={archivePending}
              processing={archivePending}
              type="red"
              dataTestId={`${warningModalDataTestId}-confirm-button`}
              onClick={handleArchive}
            >
              {TEXTS.CONFIRM_BUTTON_LABEL}
            </Button>
          </ButtonsWrapper>
        }
        isOpen={archiveModalOpen}
        size="medium"
        title={TEXTS.ARCHIVE_MODAL_HEADER}
        dataTestId={warningModalDataTestId}
        onClose={toggleArchiveModalOpen}
      >
        <p className={styles.archiveWarning}>
          {TEXTS.ARCHIVE_PROVIDER_WARNING}
        </p>
        {archiveError && <Info type="error">{archiveError.message}</Info>}
      </WarningModal>
      <ActionMenu alignment="left-start" dataTestId="provider-menu">
        <MenuItem
          to={`/${MAIN_ROUTE}/details/${id}`}
          dataTestId="provider-details-menu-item"
        >
          {MENU.APP.DETAILS}
        </MenuItem>
        <RestrictedArea
          allowed={[ROLES.tivo.provider, ROLES.tivo.bizDev]}
          showFallback={false}
        >
          <MenuItem
            to={`/${MAIN_ROUTE}/edit/${id}`}
            dataTestId="provider-edit-menu-item"
          >
            {MENU.APP.EDIT}
          </MenuItem>
        </RestrictedArea>
        <RestrictedArea allowed={[ROLES.tivo.provider]} showFallback={false}>
          <MenuItem
            to={`/${MAIN_ROUTE}/create/${id}/variant`}
            dataTestId="provider-create-variant-menu-item"
          >
            {MENU.APP.VARIANT}
          </MenuItem>
        </RestrictedArea>
        <RestrictedArea allowed={[ROLES.tivo.admin]} showFallback={false}>
          <MenuItem
            dataTestId="provider-delete-menu-item"
            onClick={() => {
              archiveReset();
              toggleArchiveModalOpen();
            }}
          >
            {MENU.APP.DELETE}
          </MenuItem>
        </RestrictedArea>
      </ActionMenu>
    </>
  );
}
